import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { getServiceIcon, getAccent } from "@/lib/serviceIcons";
import ServiceOnboardingModal from "@/components/app/ServiceOnboardingModal";

const badgeCls = {
  managed: "badge-managed", ai: "badge-ai", automation: "badge-auto",
  "self-serve": "badge-self", "pre-made": "badge-pre",
};

export default function ServiceDetail({ slug }) {
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [showFlow, setShowFlow] = useState(false);

  useEffect(() => {
    api.getService(slug).then(setService).catch(() => navigate("/app/directory"));
  }, [slug, navigate]);

  if (!service) return <div className="h-full flex items-center justify-center text-zinc-500">Loading…</div>;

  const Icon = getServiceIcon(service.icon);
  const accent = getAccent(service.accent);
  const packages = service.packages || [];
  const startPrice = packages.length ? Math.min(...packages.map((p) => p.price)) : service.price;

  return (
    <div className="h-full overflow-y-auto relative hide-scrollbar">
      <div className="absolute inset-0 bg-gradient-to-b from-primary-900/10 to-transparent pointer-events-none h-64" />
      <div className="sticky top-0 z-30 glass-nav border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/app/directory")} className="text-zinc-400 hover:text-white font-medium text-sm flex items-center transition-colors">
          <ArrowLeft size={16} className="mr-2" /> Back to Directory
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 pb-24 relative z-10 fade-in">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <div className="flex items-start gap-6">
              <div className={`w-20 h-20 rounded-2xl border flex items-center justify-center shadow-inner flex-shrink-0 ${accent.bg} ${accent.text} ${accent.border}`}>
                <Icon size={36} />
              </div>
              <div>
                <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider mb-3 inline-block ${badgeCls[service.type] || "badge-self"}`}>{service.badge}</span>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4 font-display">{service.title}</h1>
                <p className="text-lg text-zinc-400 leading-relaxed">{service.long_desc}</p>
              </div>
            </div>

            <div className="glass-panel p-8 rounded-3xl border border-white/5">
              <h3 className="text-xl font-bold text-white mb-6">What's Included</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(service.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-3 text-zinc-300">
                    <span className="w-6 h-6 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 flex items-center justify-center flex-shrink-0 mt-0.5"><Check size={13} /></span>
                    <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass-panel p-8 rounded-3xl border border-white/5">
              <h3 className="text-xl font-bold text-white mb-2">Packages</h3>
              <p className="text-sm text-zinc-400 mb-6">Choose a starting level, then add options during checkout to build your price.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {packages.map((p) => (
                  <div key={p.id} className="p-5 rounded-2xl bg-black/30 border border-white/10">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-white text-lg">{p.name}</span>
                      <span className="text-xl font-extrabold text-white">${p.price.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-zinc-400 mb-4">{p.desc}</p>
                    <ul className="space-y-1.5">
                      {p.features.map((ft) => (<li key={ft} className="text-xs text-zinc-300 flex items-center gap-2"><Check size={12} className="text-green-400 flex-shrink-0" /> {ft}</li>))}
                    </ul>
                  </div>
                ))}
              </div>
              {(service.addons || []).length > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Optional Add-ons</h4>
                  <div className="flex flex-wrap gap-2">
                    {service.addons.map((a) => (
                      <span key={a.id} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-zinc-300">{a.name} <span className="text-zinc-500">+${a.price}</span></span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="glass-panel p-8 rounded-3xl border border-white/5">
              <h3 className="text-xl font-bold text-white mb-6">How It Works</h3>
              <div className="flex flex-wrap gap-3">
                {(service.workflow_stages || []).map((st, i) => (
                  <div key={st} className="flex items-center">
                    <div className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-zinc-300 font-medium">
                      <span className="text-primary-400 font-bold mr-2">{i + 1}</span>{st}
                    </div>
                    {i < service.workflow_stages.length - 1 && <span className="mx-1 text-zinc-600">→</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="glass-panel p-8 rounded-3xl border border-white/10 sticky top-24 shadow-2xl">
              <div className="mb-2 text-xs font-bold text-zinc-500 uppercase tracking-wider">Starting at</div>
              <div className="flex items-end gap-1 mb-6 pb-6 border-b border-white/10">
                <span className="text-5xl font-extrabold text-white">${startPrice.toLocaleString()}</span>
                <span className="text-sm font-medium text-zinc-500 mb-2">/{service.billing}</span>
              </div>
              <button onClick={() => setShowFlow(true)} className="w-full py-3.5 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition-colors shadow-[0_0_15px_rgba(59,130,246,0.4)] border border-primary-400/50 mb-3">Add to Profile</button>
              <p className="text-xs text-zinc-500 text-center leading-relaxed">You'll share your project details and build your package before paying.</p>
              <p className="text-xs text-zinc-500 text-center mt-4 flex items-center justify-center"><ShieldCheck size={13} className="mr-1" /> Covered by DONDRÁE Guarantee.</p>
            </div>
          </div>
        </div>
      </div>

      <ServiceOnboardingModal open={showFlow} service={service} onClose={() => setShowFlow(false)} onDone={() => navigate("/app/workspaces")} />
    </div>
  );
}
